import { formatCurrency } from '@erp/shared-utils';
import { T, Var } from 'gt-react';
import {
  BreakdownGroup,
  getUTCMonthProjectedStatus,
} from 'src/utils/rebateForecastingUtils';
import type { BreakdownCell } from '../../common/breakdown-tables/BreakdownTable';
import {
  isYOYValue,
  aggregateYOYValues,
  extractNumericValue,
} from '../../common/breakdown-tables/indexing';
// Legacy type for compatibility
type BreakdownTableHeader = {
  name: string;
  isSubHeader?: boolean;
  isGroupHeader?: boolean;
  groupId?: string;
  childHeaders?: BreakdownTableHeader[];
  referenceId?: string;
  excludeFromColumnTotals?: boolean;
  secondaryDimensionCount?: number;
};

export const addSeparatorToValues = (
  values:
    | Record<
        string,
        number | { current: number; previous: number; delta: number } | null
      >
    | undefined,
  separatorIndex: number,
  shouldAddSeparator: boolean,
): Record<
  string,
  number | { current: number; previous: number; delta: number } | null
> => {
  if (!values || !shouldAddSeparator) return values || {};

  const entries = Object.entries(values);
  const beforeSeparator = entries.slice(0, separatorIndex);
  const afterSeparator = entries.slice(separatorIndex);

  return {
    ...Object.fromEntries(beforeSeparator),
    '|': null,
    ...Object.fromEntries(afterSeparator),
  };
};

interface MonthMetadataSimple {
  month: string;
  isProjected: boolean;
}

export const addMonthSeparators = (originalData: {
  headers: string[];
  groups: BreakdownGroup[];
  monthMetadata: MonthMetadataSimple[];
}) => {
  const actualMonths = originalData.monthMetadata.filter((m) => !m.isProjected);
  const projectedMonths = originalData.monthMetadata.filter(
    (m) => m.isProjected,
  );
  const separatorNeeded = actualMonths.length > 0 && projectedMonths.length > 0;

  const separatorIndex = actualMonths.length;
  const headers = [...originalData.headers];
  if (separatorNeeded) headers.splice(separatorIndex, 0, '|');

  const groups = originalData.groups.map((group) => ({
    ...group,
    values: addSeparatorToValues(group.values, separatorIndex, separatorNeeded),
    rows: group.rows?.map((row) => ({
      ...row,
      values: addSeparatorToValues(row.values, separatorIndex, separatorNeeded),
    })),
  }));

  return { ...originalData, headers, groups };
};

export const createDataCellRenderer =
  (
    monthMetadata: MonthMetadataSimple[],
    currentYear?: number,
    previousYear?: number,
  ) =>
  // eslint-disable-next-line react/display-name
  (
    value: number | { current: number; previous: number; delta: number } | null,
    context: { header: { name: string } },
  ) => {
    if (context.header.name === '|')
      return (
        <span className="text-xs text-muted-foreground">
          <T context="visual separator between actual and projected months in rebate table">
            |
          </T>
        </span>
      );

    // Handle YOY preset: vertically stacked cells
    if (value !== null && typeof value === 'object' && 'current' in value) {
      const { current, previous, delta } = value;
      const isProjected = getUTCMonthProjectedStatus(
        monthMetadata,
        context.header.name,
      );

      return (
        <div className="flex flex-col gap-0.5 py-1">
          {/* Current year value */}
          <span
            className={`text-xs font-medium ${
              current > 0
                ? isProjected
                  ? 'text-blue-600'
                  : 'text-black-600'
                : current < 0
                  ? 'text-red-600'
                  : 'text-muted-foreground'
            }`}
          >
            {currentYear !== undefined ? `${currentYear} ` : ''}
            {formatCurrency(current)}
          </span>
          {/* Previous year value */}
          <span className="text-xs text-muted-foreground">
            {previousYear !== undefined ? `${previousYear} ` : ''}
            {formatCurrency(previous)}
          </span>
          {/* Delta value - color coded */}
          <span
            className={`text-xs font-medium ${
              delta > 0
                ? 'text-black-600'
                : delta < 0
                  ? 'text-red-600'
                  : 'text-muted-foreground'
            }`}
          >
            <T context="rebate analytics, year-over-year change amount">
              Delta <Var>{(delta >= 0 ? '+' : '') + formatCurrency(delta)}</Var>
            </T>
          </span>
        </div>
      );
    }

    // Handle regular cells (number or null)
    if (value == null || (typeof value === 'number' && Math.abs(value) < 0.01))
      return (
        <span className="text-xs text-muted-foreground">
          <T context="empty amount placeholder in rebate breakdown cell">-</T>
        </span>
      );

    const numericValue = value as number;
    const isProjected = getUTCMonthProjectedStatus(
      monthMetadata,
      context.header.name,
    );
    let textColorClass = 'text-muted-foreground';
    if (numericValue > 0)
      textColorClass = isProjected ? 'text-blue-600' : 'text-black-600';
    else if (numericValue < 0) textColorClass = 'text-red-600';

    return (
      <span className={`text-xs font-medium ${textColorClass}`}>
        {formatCurrency(numericValue)}
      </span>
    );
  };

/**
 * Check if a header name represents a delta column (year-over-year delta)
 */
function isDeltaColumn(headerName: string): boolean {
  return headerName.includes(' Δ');
}

/**
 * Add reference IDs to flat headers (when no secondary X dimension)
 * Returns headers with numeric reference IDs (1, 2, 3, ...)
 */
export function addReferenceIdsToFlatHeaders(
  headers: string[],
): BreakdownTableHeader[] {
  return headers.map((header, index) => ({
    name: header,
    isSubHeader: false,
    referenceId: String(index + 1),
    excludeFromColumnTotals: isDeltaColumn(header),
  }));
}

/**
 * Add reference IDs to grouped headers (when secondary X dimension exists)
 * Returns grouped headers with letter reference IDs (A, B, C, ...)
 */
export function addReferenceIdsToGroupedHeaders(
  groupedHeaders: BreakdownTableHeader[],
): BreakdownTableHeader[] {
  return groupedHeaders.map((header, index) => {
    if (header.isGroupHeader) {
      const letter = String.fromCharCode(65 + index); // 65 = 'A'
      return {
        ...header,
        referenceId: letter,
        // Mark child headers that are delta columns
        childHeaders: header.childHeaders?.map((child) => ({
          ...child,
          excludeFromColumnTotals: isDeltaColumn(child.name),
        })),
      };
    }
    return header;
  });
}

/**
 * Aggregate cell values, preserving YOY objects when all values are YOY
 * Falls back to numeric sum for mixed or numeric-only values
 */
export function aggregateCellValuesWithYOY(
  values: BreakdownCell['value'][],
): BreakdownCell['value'] {
  const yoyValues: { current: number; previous: number; delta: number }[] = [];
  const numericValues: number[] = [];

  values.forEach((value) => {
    if (value === null || value === undefined) return;
    if (isYOYValue(value)) {
      yoyValues.push(value);
    } else {
      const numValue = extractNumericValue(value);
      if (!isNaN(numValue)) {
        numericValues.push(numValue);
      }
    }
  });

  // If all values are YOY objects, return aggregated YOY object
  if (yoyValues.length > 0 && numericValues.length === 0) {
    return aggregateYOYValues(yoyValues);
  }

  // Otherwise, aggregate numeric values (including extracted from YOY if mixed)
  if (yoyValues.length > 0) {
    yoyValues.forEach((yoy) => numericValues.push(yoy.current));
  }
  return numericValues.reduce((sum, val) => sum + val, 0);
}
